import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { HotelResultsList } from './HotelResultsList';
import { useHotelSearch } from '../hooks/useHotelSearch';
import type { HotelSearchFormValues } from '../types/hotels.presentation';
import { CHILDREN_AGES_PARAM, parseChildrenAgesParam } from '../utils/childrenAgesParam';

const HOTELS_BASE_PATH = '/buscarxhoteles';

const parseNumberParam = (value: string | null, fallback: number) => {
  if (!value) return fallback;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseHotelQueryFromSearch = (search: string): HotelSearchFormValues | null => {
  const params = new URLSearchParams(search);
  const destination = params.get('destination');
  const destinationId = params.get('destinationId');
  const countryCode = params.get('countryCode');
  const checkIn = params.get('checkIn');
  const checkOut = params.get('checkOut');

  if (!destination || !destinationId || !countryCode || !checkIn || !checkOut) {
    return null;
  }

  return {
    destination,
    destinationId,
    countryCode,
    checkIn,
    checkOut,
    adults: parseNumberParam(params.get('adults'), 1),
    children: parseNumberParam(params.get('children'), 0),
    childrenAges: parseChildrenAgesParam(params.get(CHILDREN_AGES_PARAM)),
    rooms: parseNumberParam(params.get('rooms'), 1),
  };
};

export function HotelResultsFromUrl() {
  const { search: locationSearch } = useLocation();
  const { onSearchCount, registerOnSearch } = useGlobal();
  const { message, offers, retry, search, status, expiresAt } = useHotelSearch();
  const executedSearchRef = useRef('');
  const currentDatesRef = useRef<{
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
  }>({});

  const handleNavigateHome = useCallback(() => {
    window.location.href = HOTELS_BASE_PATH;
  }, []);

  useEffect(() => {
    const query = parseHotelQueryFromSearch(locationSearch);

    if (query) {
      if (onSearchCount === 0) {
        registerOnSearch();
      }

      if (executedSearchRef.current === locationSearch) return;
      executedSearchRef.current = locationSearch;

      currentDatesRef.current = {
        checkIn: query.checkIn,
        checkOut: query.checkOut,
        adults: query.adults,
        children: query.children,
      };

      search(query);
    }
  }, [locationSearch, onSearchCount, registerOnSearch, search]);

  return (
    <section aria-live='polite' className='mx-auto px-[2vw] py-8'>
      <HotelResultsList
        adults={currentDatesRef.current.adults}
        checkIn={currentDatesRef.current.checkIn}
        checkOut={currentDatesRef.current.checkOut}
        childrenCount={currentDatesRef.current.children}
        expiresAt={expiresAt}
        message={message}
        offers={offers}
        onNavigateHome={handleNavigateHome}
        onRetry={retry}
        status={status}
      />
    </section>
  );
}
